import { useMutation } from "@apollo/client";
import {
  Button,
  Center,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  InternalAdminEditTagMutation,
  InternalAdminEditTagMutationVariables,
} from "gql/graphql";
import { memo, useCallback } from "react";
import React, { FieldValues, useForm } from "react-hook-form";
import colors from "app/colors";
import {
  NEW_TAG_NAME_REGEX_MULTIPLE_TAGS,
  SERVER_ERROR,
} from "modules/utils/Constants";
import { useOverlay } from "modules/providers/overlay-provider";
import { getMessageFromError } from "modules/utils/Functions";
import INTERNAL_ADMIN_EDIT_TAG_MUTATION from "../_networking/internal-admin-edit-tag";
import useQueryString from "modules/hooks/use-query-string";
import useInternalAdminTags from "../hooks/use-internal-admin-tags";
import useInternalAdminTagCategoryList from "../hooks/use-internal-admin-tag-category-list";

interface IProps {
  tagId: string;
}

const InternalAdminEditTagModal = ({ tagId }: IProps) => {
  const { closeModal } = useOverlay();
  const { qsOrganizationId } = useQueryString();

  const [internalAdminEditTagMutationAPI, { loading }] = useMutation<
    InternalAdminEditTagMutation,
    InternalAdminEditTagMutationVariables
  >(INTERNAL_ADMIN_EDIT_TAG_MUTATION);

  const { tag } = useInternalAdminTags(tagId);
  const { tagCategories } = useInternalAdminTagCategoryList({
    organizationId: qsOrganizationId,
    input: {
      limit: 1000000,
      offset: 0,
    },
  });

  const {
    handleSubmit,
    formState: { errors },
    register,
    watch,
    setError,
    clearErrors,
  } = useForm({
    mode: "onSubmit",
  });

  const onSubmit = useCallback(
    async ({ name, tagCategoryId }: FieldValues) => {
      if (!qsOrganizationId) return;
      if (!tag) return;
      internalAdminEditTagMutationAPI({
        variables: {
          organizationId: qsOrganizationId,
          id: tag.id,
          input: {
            name,
            tagCategoryId,
          },
        },
        onError: (error: Error) => {
          setError(SERVER_ERROR, {
            message: error.message,
          });
        },
        onCompleted: () => {
          closeModal();
        },
      });
    },
    [
      closeModal,
      internalAdminEditTagMutationAPI,
      qsOrganizationId,
      setError,
      tag,
    ],
  );

  if (!tag || tagCategories.length === 0) {
    return null;
  }

  return (
    <Modal isOpen onClose={closeModal} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalHeader>
          <Center>
            <Text>Edit Tag</Text>
          </Center>
        </ModalHeader>
        <ModalBody>
          <form id="admin-tag -form" onSubmit={handleSubmit(onSubmit)}>
            <VStack alignItems="flex-start" spacing={4}>
              <FormControl isInvalid={!!errors?.name}>
                <FormLabel fontWeight={700}>Name(s)</FormLabel>
                <InputGroup>
                  <InputLeftElement color={colors.gray30} mx={0}>
                    #
                  </InputLeftElement>
                  <Input
                    id="name"
                    pl={8}
                    autoComplete="off"
                    defaultValue={tag ? tag.name : undefined}
                    {...register("name", {
                      pattern: {
                        value: NEW_TAG_NAME_REGEX_MULTIPLE_TAGS,
                        message:
                          "Only lowercase letters (a-z), numbers (0-9), hyphens (-), periods (.), and underscores (_) are allowed.",
                      },
                      required: {
                        message: "a name is required",
                        value: true,
                      },
                    })}
                  />
                </InputGroup>
                <FormErrorMessage>
                  {getMessageFromError(errors?.name?.message)}
                </FormErrorMessage>
              </FormControl>

              {tag.tagCategoryId && (
                <FormControl isInvalid={!!errors?.tagCategoryId}>
                  <FormLabel>
                    <Text fontWeight={700}>Category</Text>
                  </FormLabel>
                  <Select
                    id="tagCategoryId"
                    defaultValue={tag.tagCategoryId}
                    isDisabled={watch("parentTagId")}
                    placeholder="Select Category"
                    {...register("tagCategoryId", {
                      required: {
                        message:
                          "Either a parent tag or a Category is required",
                        value: !watch("parentTagId"),
                      },
                    })}
                  >
                    {tagCategories.map((tagCategory) => (
                      <option key={tagCategory.id} value={tagCategory.id}>
                        {tagCategory.name}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>
                    {getMessageFromError(errors.tagCategoryId?.message)}
                  </FormErrorMessage>
                </FormControl>
              )}
            </VStack>
            {errors.SERVER_ERROR && (
              <Text color={colors.red}>
                {getMessageFromError(errors?.SERVER_ERROR?.message)}
              </Text>
            )}
            <Center pt={5}>
              <HStack>
                <Button
                  variant="cancel"
                  height={8}
                  w={28}
                  onClick={(e) => {
                    e.stopPropagation();
                    closeModal();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  height={8}
                  w={28}
                  onClick={() => {
                    clearErrors([SERVER_ERROR]);
                  }}
                  type="submit"
                >
                  {loading ? <Spinner /> : "Save"}
                </Button>
              </HStack>
            </Center>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(InternalAdminEditTagModal);
